import React, { FC, memo } from 'react';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import MapIcon from '~Assets/icons/map.svg';
import { useButton, useI18n } from '~Hooks';
import { Box, Button, GreenButton } from '~Styles';
import { BlueprintChange } from '../blueprintStorage';
import ChangeRow from './ChangeRow';
import { InfoBox } from './styles';

interface Props {
  isShowing: boolean;
  isHandy: boolean;
  /** Name of the blueprint about to be applied */
  blueprintName: string;
  /** Settings whose value will change when the blueprint is applied */
  changes: BlueprintChange[];
  onConfirm: () => void;
  onClose: () => void;
}

/** Confirms overwriting this device's settings with a blueprint; every change is listed in a scrolling box */
const ApplyBlueprintModal: FC<Props> = ({
  isShowing,
  isHandy,
  blueprintName,
  changes,
  onConfirm,
  onClose,
}) => {
  const { i18n } = useI18n();

  const confirmButton = useButton({ onClick: onConfirm });
  const cancelButton = useButton({ onClick: onClose });

  return (
    <Modal isShowing={isShowing} hide={onClose}>
      <ModalWrapper
        padding={isHandy ? 'large' : '3xl'}
        width={isHandy ? '340px' : '480px'}
        gap="small"
        header={{
          icon: MapIcon,
          title: blueprintName,
          subtitle: i18n('Settings.Blueprints.Apply.Title'),
        }}>
        <InfoBox fluid display="flex" flexDirection="column" alignItems="center" gap="xs">
          <Paragraph level={4} color="var(--colorPOSClassic11)">
            {i18n('Settings.Blueprints.ChangeCount', changes.length)}
          </Paragraph>

          {changes.map((change) => (
            <ChangeRow
              key={change.key}
              change={change}
              color="var(--colorPOSClassic11)"
              isHandy={isHandy}
            />
          ))}
        </InfoBox>

        <Box fluid display="flex" gap="xs" marginTop="small">
          <Button fluid {...cancelButton}>
            {i18n('General.Cancel')}
          </Button>
          <GreenButton fluid {...confirmButton}>
            {i18n('General.Confirm')}
          </GreenButton>
        </Box>
      </ModalWrapper>
    </Modal>
  );
};

export default memo(ApplyBlueprintModal);
